import { useUpdatePatientDetails } from '@/components/Patient/hooks/useUpdatePatientDetails';
import { FF_PATIENT_DETAILS_SKELETON_ENABLED } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import { useAppStore } from '@/state-management';
import type { DirtyFields } from '@/utils/reactHookForm';
import { dirtyValues } from '@/utils/reactHookForm';
import { zodResolver } from '@hookform/resolvers/zod';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import { Box, Skeleton, styled } from '@mui/material';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form-mui';
import { FormContainer, SwitchElement, TextareaAutosizeElement, useForm } from 'react-hook-form-mui';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import type { FormFields } from './MedicalInformation.schema';
import { schema } from './MedicalInformation.schema';

export type MedicalInformationProps = {
  editable?: boolean;
  defaultValues?: FormFields;
  onSubmit?: (data: FormFields) => void;
  isLoading?: boolean;
};

const StyledSwitchElement = styled(SwitchElement)({
  marginLeft: 0,
  width: '100%',
  justifyContent: 'space-between',
});

const MedicalInformationSkeleton = () =>
  Array.from({ length: 3 }).map((_, index) => (
    <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={2.5}>
      <Skeleton variant="rounded" width={120} height={20} />
      <Skeleton variant="rounded" width={36} height={16} sx={{ mr: 3, display: { xs: 'none', md: 'block' } }} />
    </Box>
  ));

export const MedicalInformation = ({ editable, defaultValues, isLoading }: MedicalInformationProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_MEDICAL_INFO;
  const { flags } = useFeatureFlags();
  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];

  const formContext = useForm<FormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
    control,
  } = formContext;

  const getPatientId = useAppStore.use.getPatientId();

  const { isPending, isError, isSuccess, mutateAsync } = useUpdatePatientDetails(FORM_NAME);

  const isEffectivelyEditable = editable;

  const { cannabisNaive, hasAllergy, allergies, hasCardioDisease, cardioDisease } = useWatch({
    control,
  });

  const showAllergyField = Boolean(hasAllergy);
  const showCardioDiseaseField = Boolean(hasCardioDisease);

  const handleOnSubmit = handleSubmit(
    ({ cannabisNaive, hasAllergy, hasCardioDisease, allergies, cardioDisease }: FieldValues) => {
      const patientId = getPatientId();

      // Create a shallow copy to modify
      const modifiedDirtyFields = { ...dirtyFields };

      if (!hasAllergy) {
        modifiedDirtyFields.allergies = true;
      }
      if (!hasCardioDisease) {
        modifiedDirtyFields.cardioDisease = true;
      }

      if (patientId) {
        const payload = dirtyValues(modifiedDirtyFields as DirtyFields<unknown>, {
          cannabisNaive,
          hasAllergy,
          hasCardioDisease,
          allergies: hasAllergy ? allergies : '',
          cardioDisease: hasCardioDisease ? cardioDisease : '',
        });
        mutateAsync(patientId, payload);
      }
    },
  );

  const textAreaDebounceSubmit = useDebouncedCallback(handleOnSubmit, 1000, [handleOnSubmit]);

  /**
   * manually trigger mutation when this fields changes
   */
  useEffect(() => {
    if (isDirty) {
      handleOnSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cannabisNaive, hasAllergy, hasCardioDisease]);

  useEffect(() => {
    if (isDirty) {
      textAreaDebounceSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allergies, cardioDisease]);

  /**
   * Need to reset the form to clear the form isDirty flag and dirtyField
   * otherwise, it will continue to include those fields in the payload
   */
  useEffect(() => {
    if (!isPending && isSuccess) {
      reset({
        ...formContext.getValues(),
        allergies: hasAllergy ? allergies : '',
        cardioDisease: hasCardioDisease ? cardioDisease : '',
      });
    }
  }, [allergies, cardioDisease, formContext, hasAllergy, hasCardioDisease, isPending, isSuccess, reset]);

  return (
    <FormSection
      title="General"
      formContext={formContext}
      formSectionStatus={{ isPending, isError, isSuccess }}
      data-testid="medical-information"
      noBorder
    >
      {isLoading && isSkeletonEnabled ? (
        <MedicalInformationSkeleton />
      ) : (
        <FormContainer formContext={formContext}>
          <Box data-testid={'medical-information-form'}>
            <StyledSwitchElement
              label="Cannabis Naive"
              labelPlacement="start"
              name="cannabisNaive"
              disabled={!isEffectivelyEditable}
            />
            <StyledSwitchElement
              label="Allergies"
              labelPlacement="start"
              name="hasAllergy"
              disabled={!isEffectivelyEditable}
            />
            {showAllergyField && (
              <TextareaAutosizeElement
                name="allergies"
                fullWidth
                disabled={!isEffectivelyEditable}
                resizeStyle="vertical"
              />
            )}
            <StyledSwitchElement
              label="Cardiovascular Disease"
              labelPlacement="start"
              name="hasCardioDisease"
              disabled={!isEffectivelyEditable}
            />
            {showCardioDiseaseField && (
              <TextareaAutosizeElement
                name="cardioDisease"
                fullWidth
                disabled={!isEffectivelyEditable}
                resizeStyle="vertical"
              />
            )}
          </Box>
        </FormContainer>
      )}
    </FormSection>
  );
};
